import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MondayFinalModal({ 
  open, 
  onClose, 
  clientId, 
  clientName,
  transactionCount,
  currentBalance,
  onSuccess
}) {
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState(null);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${API_URL}/api/settlements/monday-final`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          client_id: clientId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setConfirmed(true);
        toast.success(`Monday Final completed. ${data.transactions_locked} transactions locked.`);
        
        // Call success callback to refresh parent
        if (onSuccess) {
          onSuccess(data);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to perform Monday Final');
      }
    } catch (error) {
      console.error('Error performing Monday Final:', error);
      toast.error('Error performing Monday Final');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setConfirmed(false);
    setResult(null);
    onClose(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {confirmed ? (
              <>
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                Monday Final Complete
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Confirm Monday Final
              </>
            )}
          </DialogTitle>
          {!confirmed && (
            <DialogDescription>
              This action will lock all visible transactions for this account.
            </DialogDescription>
          )}
        </DialogHeader>

        {confirmed && result ? (
          <div className="space-y-4">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client:</span>
                  <span className="font-medium">{clientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Transactions Locked:</span>
                  <span className="font-medium">{result.transactions_locked}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Opening Balance:</span>
                  <span className="font-mono">{result.opening_balance?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="text-muted-foreground">New Old Balance:</span>
                  <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                    {result.closing_balance?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              All transactions have been locked and hidden. The account now shows a clean slate with the new balance.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client:</span>
                  <span className="font-medium">{clientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Transactions to Lock:</span>
                  <span className="font-medium">{transactionCount || 'All visible'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current Balance:</span>
                  <span className="font-mono">{currentBalance?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
              <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">This action will:</p>
                <ul className="list-disc list-inside mt-1 space-y-1 text-muted-foreground">
                  <li>Lock all current visible transactions</li>
                  <li>Move current balance to Old Balance</li>
                  <li>Hide locked transactions from view</li>
                  <li>Start a fresh period with clean slate</li>
                </ul>
              </div>
            </div>
            
            <p className="text-xs text-red-500 font-medium text-center">
              Warning: This action cannot be undone. Locked transactions are immutable.
            </p>
          </div>
        )}

        <DialogFooter>
          {confirmed ? (
            <Button onClick={handleClose}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleConfirm} 
                disabled={loading}
                className="gap-1"
              >
                {loading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Lock className="h-4 w-4" />
                    Confirm Monday Final
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
